<script lang="ts" setup>
import {
  TemplateButtonProperty,
  TemplateContentType,
  TemplateComponentType,
  WabaChannelTemplate,
} from "~/entities/template";
import { TSelectOption } from "@profeat/ui-kit/dist/components/PSelect/types";
import { computed, nextTick, onMounted, reactive, ref, watch } from "vue";
import { FileData } from "~/stores/broadcast.store";
import { $t } from "~/i18n";
import { BroadcastVariablesMap } from "~/entities/broadcast";
import { TemplateVariablesMap } from "./types";
import VariableTextInput from "./components/VariableTextInput.vue";

type CallbackT<T extends any[] = any[]> = (...args: T) => void;
type ErrorT = Record<string, string[]>;

enum EventE {
  VALUE_CHANGED = "valueChanged",
}

class BaseDataSource<SchemaT extends Record<string, unknown>> {
  private __data__: SchemaT;
  private __listeners__: Record<string, CallbackT[]> = {};
  get data() {
    return this.__data__;
  }
  get key(): string {
    return this._dsKey;
  }
  constructor(readonly _dsKey: string, readonly initialData?: SchemaT) {
    if (initialData) {
      this.__data__ = initialData;
    }
  }
  get(key: keyof SchemaT): SchemaT[keyof SchemaT] | null {
    if (!Object.prototype.hasOwnProperty.call(this.__data__, key)) {
      return null;
    }
    return this.__data__[key as keyof SchemaT];
  }
  set(key: keyof SchemaT, value: SchemaT[keyof SchemaT]) {
    this.__data__[key] = value;
    this.emit(EventE.VALUE_CHANGED, this.__data__);
  }
  keys() {
    return Object.keys(this.__data__);
  }
  on(key: string, call: CallbackT) {
    if (!this.__listeners__[key]) {
      this.__listeners__[key] = [call];
    }
    this.__listeners__[key].push(call);
  }
  emit(event: string, value: unknown) {
    for (const call of this.__listeners__[event]) {
      call(value);
    }
  }
}

const props = defineProps<{
  template: WabaChannelTemplate;
  templateVariables: BroadcastVariablesMap;
  variableGenericOptions: TSelectOption[];
  headerFile: FileData | null;
}>();

const emit = defineEmits<{
  valueChanged: [{ data: TemplateVariablesMap }];
  fileChanged: [{ data: FileData | null }];
  errorsChanged: [{ data: Record<string, ErrorT>; hasErrors: boolean }];
}>();

const EXTRACT_VARS_REGEXP = /({{).*?(}})/g;

const variablesMap = getTemplateVariablesMap(props.template);

const bodyVarsDatasource = new BaseDataSource<TemplateVariablesMap["bodyVars"]>(
  "_dsBody",
  variablesMap.bodyVars
);

const buttonVarsDatasource = new BaseDataSource<
  TemplateVariablesMap["buttonVars"]
>("_dsButtons", variablesMap.buttonVars);

const headerVarsDataSource = new BaseDataSource<
  TemplateVariablesMap["headerVars"]
>("_dsHeader", variablesMap.headerVars);

const errors = reactive<Record<string, ErrorT>>({
  [bodyVarsDatasource.key]: {},
  [buttonVarsDatasource.key]: {},
  [headerVarsDataSource.key]: {},
});

const hasErrors = computed<boolean>(() => {
  let is = false;
  for (const key of Object.keys(errors)) {
    if (Object.keys(errors[key]).length) {
      is = true;
    }
  }
  if (props.template.type !== TemplateContentType.TEXT) {
    if (!headerFileValue.value) {
      is = true;
    }
  }
  return is;
});

const headerFileValue = ref<FileData | null>(props.headerFile);

const filePickerTitle = computed(() =>
  props.headerFile
    ? $t(
        `broadcast.uploadFile.${props.template.type.toLowerCase()}.pickAnother`
      )
    : $t(`broadcast.uploadFile.${props.template.type.toLowerCase()}.title`)
);

function getTemplateVariablesMap(template: WabaChannelTemplate) {
  const regex = /^{{|}}$/g;
  return {
    headerVars: Object.fromEntries(
      template.header?.match(EXTRACT_VARS_REGEXP)?.map((variable: string) => {
        const key = variable.replace(regex, "");
        // todo: распарсить все, что в {{}}
        return [
          variable.replace(regex, ""),
          props.templateVariables.headerVars[key] || "",
        ];
      }) || []
    ),
    bodyVars: Object.fromEntries(
      template.body?.match(EXTRACT_VARS_REGEXP)?.map((variable: string) => {
        const key = variable.replace(regex, "");
        // todo: распарсить все, что в {{}}
        return [
          variable.replace(regex, ""),
          props.templateVariables.bodyVars[key] || "",
        ];
      }) || []
    ),
    buttonVars: Object.fromEntries(
      template.buttons
        ?.map((buttonProps: TemplateButtonProperty) => buttonProps?.text || "")
        ?.join()
        ?.match(EXTRACT_VARS_REGEXP)
        ?.map((variable: string) => {
          const key = variable.replace(regex, "");
          // todo: распарсить все, что в {{}}
          return [
            variable.replace(regex, ""),
            props.templateVariables.buttonVars[key] || "",
          ];
        }) || []
    ),
    footerVars: {},
  };
}

headerVarsDataSource.on(
  "valueChanged",
  (data: TemplateVariablesMap["headerVars"]) => {
    syncVariables({ headerVars: data });
    revalidate(headerVarsDataSource);
    emit("valueChanged", { data: variablesMap });
  }
);

bodyVarsDatasource.on(
  "valueChanged",
  (data: TemplateVariablesMap["bodyVars"]) => {
    syncVariables({ bodyVars: data });
    revalidate(bodyVarsDatasource);
    emit("valueChanged", { data: variablesMap });
  }
);

buttonVarsDatasource.on(
  "valueChanged",
  (data: TemplateVariablesMap["buttonVars"]) => {
    syncVariables({ buttonVars: data });
    revalidate(buttonVarsDatasource);
    emit("valueChanged", { data: variablesMap });
  }
);

watch(
  () => headerFileValue.value,
  (value) => {
    emit("fileChanged", { data: value });
  },
  {
    deep: true,
  }
);

watch(
  () => errors,
  (value) => {
    emit("errorsChanged", { data: value, hasErrors: hasErrors.value });
  },
  {
    deep: true,
  }
);

watch(
  () => hasErrors.value,
  (value) => {
    emit("errorsChanged", { data: errors, hasErrors: value });
  }
);

onMounted(() => {
  nextTick(() => {
    emit("valueChanged", { data: variablesMap });
    revalidate(headerVarsDataSource);
    revalidate(bodyVarsDatasource);
    revalidate(buttonVarsDatasource);
    emit("errorsChanged", { data: errors, hasErrors: hasErrors.value });
  });
});

function syncVariables(variables: Partial<TemplateVariablesMap>) {
  Object.assign(variablesMap, variables);
}

function revalidate(ds: BaseDataSource<any>): void {
  const _errors: ErrorT = {};
  for (const key of ds.keys()) {
    if (!ds.data[key]) {
      _errors[key] = [$t(`validation.requiredField`)];
    }
  }
  errors[ds.key] = _errors;
}
</script>

<template>
  <div class="header-picker" v-if="template.type !== TemplateContentType.TEXT">
    <FilePicker
      :title="filePickerTitle"
      :type="(template.type as Exclude<TemplateContentType, TemplateContentType.TEXT>)"
      :value="headerFileValue as FileData"
      @value-changed="(value: FileData | null) => headerFileValue = value"
    />
  </div>
  <div
    class="header-vars-area"
    v-if="template.header && template.type === TemplateContentType.TEXT"
  >
    <template
      v-for="key in headerVarsDataSource.keys() as (keyof TemplateVariablesMap['bodyVars'])[]"
      :key="key"
    >
      <div class="field-container">
        <VariableTextInput
          :value="headerVarsDataSource.get(key)"
          :generic-select-options="variableGenericOptions"
          :error-text="errors[headerVarsDataSource.key][key]?.join('\n')"
          :variable-key="key"
          :variable-type="TemplateComponentType.HEADER"
          @valueChanged="(value: string) => headerVarsDataSource.set(key, value)"
        ></VariableTextInput>
      </div>
    </template>
  </div>
  <div class="body-vars-area">
    <template
      v-for="key in bodyVarsDatasource.keys() as (keyof TemplateVariablesMap['bodyVars'])[]"
      :key="key"
    >
      <div class="field-container">
        <VariableTextInput
          :value="bodyVarsDatasource.get(key)"
          :generic-select-options="variableGenericOptions"
          :error-text="errors[bodyVarsDatasource.key][key]?.join('\n')"
          :variable-key="key"
          :variable-type="TemplateComponentType.BODY"
          @valueChanged="(value: string) => bodyVarsDatasource.set(key, value)"
        ></VariableTextInput>
      </div>
    </template>
  </div>
  <div class="button-vars-area">
    <template
      v-for="key in buttonVarsDatasource.keys() as (keyof TemplateVariablesMap['buttonVars'])[]"
      :key="key"
    >
      <div class="field-container">
        <VariableTextInput
          :value="buttonVarsDatasource.get(key)"
          :generic-select-options="variableGenericOptions"
          :error-text="errors[buttonVarsDatasource.key][key]?.join('\n')"
          :variable-key="key"
          :variable-type="TemplateComponentType.BODY"
          @valueChanged="(value: string) => buttonVarsDatasource.set(key, value)"
        ></VariableTextInput>
      </div>
    </template>
  </div>
</template>
<style lang="scss" scoped>
.body-vars-area {
  width: 100%;
}
.field-container {
  margin: 15px 0;
}
</style>
